import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { themeTokens, resetStyles, baseStyles } from '../../styles/tokens.css.js';
import { DEFAULT_PAYMENT_CURRENCY } from '@yebo/shared';
import type { PaymentProvider } from '@yebo/shared';
import { DEPOSIT_COPY, formatHoldDeadline } from '../../deposit-copy.js';
import { formatMoney } from '../../utils/formatting.js';

declare global {
  interface Window {
    Stripe?: (key: string) => StripeInstance;
  }
}

interface StripeInstance {
  elements(opts: { clientSecret: string; appearance?: Record<string, unknown> }): StripeElements;
  confirmPayment(opts: {
    elements: StripeElements;
    confirmParams?: { return_url?: string };
    redirect?: 'if_required';
  }): Promise<{ error?: { message: string }; paymentIntent?: { id: string; status: string } }>;
}

interface StripeElements {
  create(type: 'payment', opts?: Record<string, unknown>): StripePaymentElement;
}

interface StripePaymentElement {
  mount(el: string | HTMLElement): void;
  unmount(): void;
  on(event: string, cb: (e: any) => void): void;
}

// ── #848 — the Stripe mount must live in the LIGHT DOM ───────────────────────
//
// Stripe's Payment Element mounted inside a shadow root fires `loaderstart` and
// never `ready` (isolated 2026-09-24 on staging: the same client secret mounted
// in the light DOM is ready in ~1s, inside a shadow root it never is; forcing the
// container visible changes nothing). This component is a Lit element two or
// three shadow roots deep, so it mounts into a node it creates on the OUTERMOST
// shadow host — `<yebo-booking>` on an inline embed, `<yebo-book-now>` on the
// floating-button embed — with slot="stripe-payment"; every host in the chain
// (book-now-button.ts, booking-widget.ts) forwards that slot into this step's
// `<slot>` so the form still renders where the card fields belong.

/** The named slot the light-DOM mount node is projected through. */
export const STRIPE_MOUNT_SLOT = 'stripe-payment';
const STRIPE_MOUNT_ATTR = 'data-yebo-stripe-mount';

/** Stripe's appearance API accepts HEX / rgb() / hsl() only; anything else
 *  (e.g. a `color-mix(...)` token) is rejected with a console warning. */
export function cssColorOr(value: string | undefined | null, fallback: string): string {
  const v = (value ?? '').trim();
  if (/^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v)) return v;
  if (/^(?:rgb|hsl)a?\([^()]*\)$/i.test(v)) return v;
  return fallback;
}

/** The appearance we hand Stripe, built from the widget's CSS tokens. */
export function stripeAppearanceFrom(readVar: (name: string) => string): Record<string, unknown> {
  return {
    theme: 'night',
    variables: {
      colorPrimary: cssColorOr(readVar('--yebo-primary'), '#c4a87a'),
      fontFamily: (readVar('--yebo-font') || '').trim() || 'system-ui',
      borderRadius: (readVar('--yebo-radius') || '').trim() || '8px',
      colorBackground: cssColorOr(readVar('--yebo-bg-elevated'), '#2a2a2e'),
      colorText: cssColorOr(readVar('--yebo-text'), '#e8dcc8'),
    },
  };
}

/**
 * Wire the element's lifecycle. `loaderror` was never listened for before
 * #848, so a form that failed to load was an endless spinner with a disabled
 * Pay button and no sentence. Pure, so a fake element can drive it in a test.
 */
export function attachPaymentElementListeners(
  pe: StripePaymentElement,
  h: {
    onReady: () => void;
    onLoadError: (message: string) => void;
    /** A replaced element may still emit; ignore it (review F4). Defaults to "always current". */
    isCurrent?: () => boolean;
  },
): void {
  const current = () => (h.isCurrent ? h.isCurrent() : true);
  pe.on('ready', () => { if (current()) h.onReady(); });
  pe.on('loaderror', (e: any) => {
    if (!current()) return;
    h.onLoadError(e?.error?.message || 'The payment form could not be loaded. Please try again.');
  });
}

/** Injectable for unit tests; the defaults are the browser's. */
export interface MountNodeDeps {
  isShadowRoot: (root: Node) => boolean;
  createElement: (tag: string) => HTMLElement;
}
const browserDeps: MountNodeDeps = {
  isShadowRoot: (root) => typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot,
  createElement: (tag) => document.createElement(tag),
};

/**
 * Find (or create, once) the light-DOM node Stripe mounts into: a child of the
 * OUTERMOST shadow host carrying slot="stripe-payment". Every shadow host in
 * the chain must forward that slot (booking-widget.ts and book-now-button.ts
 * do); `mountIsProjected` below checks that it actually arrived. When the
 * element sits in no shadow root at all, the node hangs on the element itself,
 * whose own <slot> projects it. A node that already exists is reused — emptied
 * first, so a stale Stripe frame never lingers under a fresh mount.
 */
export function resolveLightDomMountNode(el: Element, deps: MountNodeDeps = browserDeps): HTMLElement {
  let node: Node = el;
  let host: Element = el;
  for (;;) {
    const root = node.getRootNode();
    if (deps.isShadowRoot(root)) { host = (root as ShadowRoot).host; node = host; } else break;
  }
  let mount = host.querySelector<HTMLElement>(`:scope > [${STRIPE_MOUNT_ATTR}]`);
  if (mount) {
    mount.replaceChildren();
  } else {
    mount = deps.createElement('div');
    mount.setAttribute(STRIPE_MOUNT_ATTR, '');
    mount.setAttribute('slot', STRIPE_MOUNT_SLOT);
    host.appendChild(mount);
  }
  return mount;
}

/**
 * True when the step's own <slot name="stripe-payment"> received the mount
 * node through the (possibly nested) slot chain. False means a wrapper in the
 * chain does not forward the slot — Stripe would be mounting into a node
 * nobody can see, so the caller refuses and says so rather than enabling Pay
 * over an empty card area (review F1).
 */
export function mountIsProjected(slot: HTMLSlotElement | null | undefined, mount: HTMLElement): boolean {
  if (!slot) return false;
  return slot.assignedElements({ flatten: true }).includes(mount);
}

let stripeLoadPromise: Promise<void> | null = null;

function loadStripeJs(): Promise<void> {
  if (window.Stripe) return Promise.resolve();
  if (stripeLoadPromise) return stripeLoadPromise;
  stripeLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Stripe.js'));
    document.head.appendChild(script);
  });
  return stripeLoadPromise;
}

@customElement('yebo-step-payment')
export class YeboStepPayment extends LitElement {
  static styles = [
    themeTokens,
    resetStyles,
    baseStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      .step-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-lg);
      }
      #payment-element {
        min-height: 200px;
      }
      .payment-error {
        color: var(--yebo-error);
        font-size: var(--yebo-font-size-sm);
        margin-top: var(--yebo-space-xs);
      }
      .actions {
        display: flex;
        justify-content: space-between;
        padding-top: var(--yebo-space-md);
      }

      /* Seat-hold notice (#480) — shown whenever a hold deadline is in play,
         on both the card-fields and hosted-redirect renderers. */
      .hold-notice {
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: var(--yebo-space-sm) var(--yebo-space-md);
        background: var(--yebo-bg-elevated);
      }
      .hold-deadline {
        margin: 0;
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        color: var(--yebo-text-heading);
      }
      .hold-policy {
        margin: var(--yebo-space-xs) 0 0;
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }

      /* Hosted-redirect payment styles (any gateway with an authorizationUrl) */
      .hosted-payment-card {
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: var(--yebo-space-lg);
        text-align: center;
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-md);
        align-items: center;
      }
      .hosted-payment-icon {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: var(--yebo-primary-light);
        color: var(--yebo-primary);
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .card-payment-total {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--yebo-space-sm);
        margin: 0 0 var(--yebo-space-md);
        padding: var(--yebo-space-sm) 0;
        border-bottom: 1px solid var(--yebo-border);
      }
      .card-payment-total-label {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
      }
      .card-payment-total-amount {
        font-size: var(--yebo-font-size-xl);
        font-weight: 600;
        color: var(--yebo-text-heading);
      }
      .hosted-payment-amount {
        font-size: var(--yebo-font-size-xl);
        font-weight: 600;
        color: var(--yebo-text-heading);
      }
      .hosted-payment-desc {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        max-width: 280px;
      }
      .hosted-pay-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--yebo-space-sm);
        padding: 12px 32px;
        border-radius: var(--yebo-radius);
        font-family: var(--yebo-font);
        font-size: var(--yebo-font-size-base);
        font-weight: 600;
        cursor: pointer;
        border: none;
        background: var(--yebo-primary);
        color: var(--yebo-bg);
        transition: background-color var(--yebo-transition);
        text-decoration: none;
      }
      .hosted-pay-btn:hover {
        background: var(--yebo-primary-hover);
      }
      .hosted-poll-status {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        display: flex;
        align-items: center;
        gap: var(--yebo-space-sm);
      }
      .hosted-poll-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--yebo-primary);
        animation: pulse 1.5s ease-in-out infinite;
      }
      @keyframes pulse {
        0%, 100% { opacity: 0.4; }
        50% { opacity: 1; }
      }
      @media (prefers-reduced-motion: reduce) {
        .hosted-poll-dot { animation: none; }
      }
      .btn-spinner {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: currentColor;
        border-radius: 50%;
        animation: btn-spin 0.6s linear infinite;
        flex-shrink: 0;
      }
      @keyframes btn-spin {
        to { transform: rotate(360deg); }
      }
      @media (prefers-reduced-motion: reduce) {
        .btn-spinner { animation: none; }
      }
      .deposit-label {
        display: inline-block;
        font-size: var(--yebo-font-size-sm);
        font-weight: 600;
        color: var(--yebo-primary);
        background: var(--yebo-primary-light);
        border-radius: calc(var(--yebo-radius) / 2);
        padding: 2px 10px;
        margin-bottom: var(--yebo-space-xs);
      }
    `,
  ];

  // Shared properties
  @property({ type: String, attribute: 'payment-provider' }) paymentProvider: PaymentProvider = 'stripe';
  @property({ type: Boolean }) loading = false;
  @property({ type: Number, attribute: 'payment-amount' }) paymentAmount = 0;
  @property({ type: String, attribute: 'payment-currency' }) paymentCurrency = DEFAULT_PAYMENT_CURRENCY;
  @property({ type: Number, attribute: 'party-size' }) partySize = 1;
  @property({ type: Number, attribute: 'kids-count' }) kidsCount = 0;
  @property({ type: Number, attribute: 'per-person-amount' }) perPersonAmount = 0;
  @property({ type: Number, attribute: 'per-kid-amount' }) perKidAmount = 0;

  // ── Payment HANDLES (D6 — the renderer is chosen by SHAPE, not by provider) ─
  // Exactly one of these is populated for any given payment:
  //   clientSecret     → card fields in-widget (Stripe Elements)
  //   authorizationUrl → a hosted page on the gateway's own origin
  // `paymentProvider` is a LABEL. It must never select a renderer: that is what
  // made a `payfast` provider carrying an authorizationUrl match neither branch
  // (break #4) — it wasn't 'ghl', so it fell into the card renderer with no
  // clientSecret to mount.
  @property({ type: String, attribute: 'client-secret' }) clientSecret = '';
  @property({ type: String, attribute: 'stripe-key' }) stripeKey = '';
  @property({ type: String, attribute: 'authorization-url' }) authorizationUrl = '';

  // DEPRECATED ALIASES — kept for one release so the legacy GHL order flow
  // (`requestGhlPaymentOrder`) keeps working unchanged. `ghlPaymentUrl` is read
  // only as a fallback for `authorizationUrl`; new callers set the latter.
  @property({ type: String, attribute: 'ghl-payment-url' }) ghlPaymentUrl = '';
  @property({ type: String, attribute: 'ghl-order-id' }) ghlOrderId = '';

  // Variable deposit type label
  @property({ type: String, attribute: 'deposit-type' }) depositType = '';

  // #480 — the seat-hold deadline, VERBATIM from /deposit/checkout. The widget
  // never invents or extends it (the 30-minute window, R6, is a server-side
  // constant and appears nowhere in this package).
  @property({ type: String, attribute: 'deposit-expires-at' }) depositExpiresAt = '';
  /** The server's honest note about what its rail does with the money (§2.1). */
  @property({ type: String, attribute: 'policy-note' }) policyNote = '';

  @state() private _stripeReady = false;
  @state() private _processing = false;
  @state() private _elementReady = false;
  @state() private _error: string | null = null;
  @state() private _hostedPaymentOpened = false;
  @state() private _hostedPolling = false;

  private _stripe: StripeInstance | null = null;
  private _elements: StripeElements | null = null;
  private _paymentElement: StripePaymentElement | null = null;
  private _pollInterval: ReturnType<typeof setInterval> | null = null;

  async connectedCallback() {
    super.connectedCallback();
    // Handle shape, not provider name (D6): boot Stripe.js only when we are
    // actually going to render card fields. A redirect handle needs nothing.
    if (!this._redirectUrl) {
      await this._initStripe();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._clearReadyWatchdog();
    this._paymentElement?.unmount();
    this._paymentElement = null;
    // #848 — the light-DOM mount node is ours; leave nothing behind on the host.
    if (this._mountNode?.hasAttribute(STRIPE_MOUNT_ATTR)) this._mountNode.remove();
    this._mountNode = null;
    this._stopPolling();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('clientSecret') && this.clientSecret && this._stripeReady) {
      this._mountPaymentElement();
    }
  }

  // ── Stripe methods ────────────────────────────────────────────────────────

  private async _initStripe() {
    if (!this.stripeKey) {
      // The 2026-07-27 class: a card renderer with no publishable key used to
      // spin for ever. Say so (review L3).
      this._error = 'Online payment is not set up for this venue yet. Please contact the restaurant to complete your booking.';
      return;
    }
    // Armed BEFORE Stripe.js loads, so a script that never settles is also
    // caught (review L3); re-armed per mount below.
    this._armReadyWatchdog();

    try {
      await loadStripeJs();
      if (!window.Stripe) throw new Error('Stripe.js not available');
      this._stripe = window.Stripe(this.stripeKey);
      this._stripeReady = true;

      if (this.clientSecret) {
        await this.updateComplete;
        this._mountPaymentElement();
      }
    } catch (err: any) {
      this._error = err.message || 'Failed to load payment form';
    }
  }

  private _mountNode: HTMLElement | null = null;
  private _readyWatchdog: ReturnType<typeof setTimeout> | null = null;
  private _mountRetried = false;

  private _clearReadyWatchdog() {
    if (this._readyWatchdog) { clearTimeout(this._readyWatchdog); this._readyWatchdog = null; }
  }

  /**
   * A form that never reports ready or an error is the #848 symptom. After
   * 20 s, re-mount ONCE in place with the same client secret (a page refresh
   * would start a second checkout while the first seat hold and any single-use
   * coupon stay claimed — review L4); if that does not come up either, say so.
   */
  private _armReadyWatchdog() {
    this._clearReadyWatchdog();
    this._readyWatchdog = setTimeout(() => {
      this._readyWatchdog = null;
      if (this._elementReady || this._error) return;
      if (!this._mountRetried && this._stripe && this.clientSecret) {
        this._mountRetried = true;
        this._mountPaymentElement();
        return;
      }
      this._error = 'The payment form is taking too long to load. Check your connection and try again in a moment — your seats are still held.';
    }, 20000);
  }

  private _mountPaymentElement() {
    if (!this._stripe || !this.clientSecret) return;

    this._paymentElement?.unmount();
    this._elementReady = false;
    this._clearReadyWatchdog();

    const readVar = (name: string) => getComputedStyle(this).getPropertyValue(name);
    this._elements = this._stripe.elements({
      clientSecret: this.clientSecret,
      appearance: stripeAppearanceFrom(readVar),
    });

    const pe = this._elements.create('payment');
    this._paymentElement = pe;
    attachPaymentElementListeners(pe, {
      isCurrent: () => this._paymentElement === pe,
      onReady: () => { this._clearReadyWatchdog(); this._elementReady = true; this._error = null; },
      onLoadError: (message) => { this._clearReadyWatchdog(); this._error = message; },
    });

    // #848 — mount in the LIGHT DOM (see the note above resolveLightDomMountNode).
    // No slot rendered (the hosted-redirect view is showing) ⇒ nothing to mount
    // into; do not hang a node on the host page for nobody (review N2).
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>(`slot[name="${STRIPE_MOUNT_SLOT}"]`);
    if (!slot) return;
    const mount = resolveLightDomMountNode(this);
    if (!mountIsProjected(slot, mount)) {
      // A wrapper in the embed chain does not forward the slot: refuse loudly
      // instead of enabling Pay over a card form nobody can see, and leave
      // nothing behind on the host page (nor an element that was never mounted).
      mount.remove();
      this._mountNode = null;
      this._paymentElement = null;
      this._error = 'The payment form cannot be shown in this embed. Please contact the venue.';
      return;
    }
    this._mountNode = mount;
    pe.mount(mount);
    this._armReadyWatchdog();
  }

  private async _confirmStripePayment() {
    if (!this._stripe || !this._elements || this._processing) return;

    this._processing = true;
    this._error = null;

    try {
      const result = await this._stripe.confirmPayment({
        elements: this._elements,
        redirect: 'if_required',
      });

      if (result.error) {
        this._error = result.error.message;
        this.dispatchEvent(new CustomEvent('payment-failed', {
          detail: { message: result.error.message },
          bubbles: true,
          composed: true,
        }));
      } else if (result.paymentIntent) {
        this.dispatchEvent(new CustomEvent('payment-succeeded', {
          detail: { paymentIntentId: result.paymentIntent.id },
          bubbles: true,
          composed: true,
        }));
      }
    } catch (err: any) {
      this._error = err.message || 'Payment failed';
    } finally {
      this._processing = false;
    }
  }

  // ── Hosted-redirect methods (any gateway with an authorizationUrl) ────────

  /** The redirect handle, whichever property carries it. Empty ⇒ card fields. */
  private get _redirectUrl(): string {
    return this.authorizationUrl || this.ghlPaymentUrl;
  }

  private _openHostedPayment() {
    const url = this._redirectUrl;
    if (!url) return;

    // Opened EXACTLY as the server built it — the widget appends no token, no
    // query parameter, nothing (DR-WIDGET-01). `noopener,noreferrer` is
    // deliberate: it is why no cross-origin channel back from the return page
    // exists or is needed (§8.3) — this tab simply keeps polling.
    window.open(url, '_blank', 'noopener,noreferrer');
    this._hostedPaymentOpened = true;

    // Start polling for payment status
    this._startPolling();
  }

  private _startPolling() {
    this._hostedPolling = true;
    this._stopPolling();

    this._pollInterval = setInterval(() => {
      this.dispatchEvent(new CustomEvent('poll-payment-status', {
        bubbles: true,
        composed: true,
      }));
    }, 5000); // Poll every 5 seconds
  }

  private _stopPolling() {
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = null;
    }
    this._hostedPolling = false;
  }

  // ── Shared methods ───────────────────────────────────────────────────────

  private _goBack() {
    this._stopPolling();
    this.dispatchEvent(new CustomEvent('step-back', { bubbles: true, composed: true }));
  }

  private _formatAmount(amount: number, currency: string): string {
    return formatMoney(amount, currency);
  }

  // ── Render helpers ───────────────────────────────────────────────────────

  private _getDepositDescription(): string {
    if (this.perPersonAmount <= 0) return 'Reservation deposit';

    const adults = this.partySize - this.kidsCount;
    const perPersonFormatted = this._formatAmount(this.perPersonAmount, this.paymentCurrency);

    if (this.kidsCount > 0) {
      const perKidFormatted = this._formatAmount(this.perKidAmount, this.paymentCurrency);
      return `${perPersonFormatted} \u00d7 ${adults} adult${adults !== 1 ? 's' : ''} + ${perKidFormatted} \u00d7 ${this.kidsCount} kid${this.kidsCount !== 1 ? 's' : ''}`;
    }

    return `${perPersonFormatted} per person \u00d7 ${this.partySize} guest${this.partySize !== 1 ? 's' : ''}`;
  }

  /**
   * The seat-hold deadline + the server's money policy note, rendered on both
   * renderers. Every word comes from `deposit-copy.ts` (ruling R3 is still
   * open, so guest wording lives in exactly one file). Renders nothing at all
   * when there is no hold — a legacy/ticketed payment is untouched.
   */
  private _renderHoldNotice() {
    if (!this.depositExpiresAt) return nothing;
    const deadline = formatHoldDeadline(this.depositExpiresAt);
    return html`
      <div class="hold-notice">
        <p class="hold-deadline">${DEPOSIT_COPY.tableHeldUntil(deadline)}</p>
        <p class="hold-policy">${this.policyNote || DEPOSIT_COPY.moneyPolicyFallback}</p>
      </div>
    `;
  }

  // ── Render ───────────────────────────────────────────────────────────────

  /** Card fields in-widget (Stripe Elements). Selected by a `clientSecret`. */
  private _renderCardFields() {
    // No spinner next to an error sentence (review F5).
    const showSpinner = !this._error && (!this._stripeReady || (this._stripeReady && this.clientSecret && !this._elementReady));
    // #953 G3 — the card step states what it charges. The hosted renderer
    // always printed the amount; this one printed nothing, so a guest bumped to
    // full price (a coupon refused at checkout) could pay a total they never
    // saw. `paymentAmount` is the server's figure (ticket/deposit checkout set
    // it). Zero means "unknown", so no amount is claimed.
    const formattedAmount = this.paymentAmount > 0
      ? this._formatAmount(this.paymentAmount, this.paymentCurrency)
      : '';

    return html`
      <div class="step-content">
        <h2 class="step-title">Payment</h2>

        ${this.depositType ? html`
          <p class="deposit-label">
            ${this.depositType === 'experience' ? 'Experience Deposit' : 'Security Hold'}
          </p>
        ` : nothing}

        ${this._renderHoldNotice()}

        ${formattedAmount ? html`
          <p class="card-payment-total">
            <span class="card-payment-total-label">Total to pay</span>
            <span class="card-payment-total-amount">${formattedAmount}</span>
          </p>
        ` : nothing}

        ${showSpinner ? html`<yebo-loading-spinner></yebo-loading-spinner>` : nothing}

        <!-- #848: the Stripe iframe lives in a light-DOM node projected through
             this slot; keep the wrapper laid out (opacity, not visibility/height)
             until Stripe reports ready, so the form can measure itself. -->
        <div id="payment-element" ?inert=${!this._elementReady} style="${!this._elementReady ? 'opacity: 0; pointer-events: none;' : ''}"><slot name="stripe-payment"></slot></div>

        ${this._error ? html`<p class="payment-error">${this._error}</p>` : nothing}
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click=${this._goBack} ?disabled=${this._processing}>Back</button>
        <button
          class="btn btn-primary"
          @click=${this._confirmStripePayment}
          ?disabled=${!this._elementReady || this._processing || this.loading}
        >
          ${this._processing ? html`<span class="btn-spinner" aria-hidden="true"></span>Processing...` : formattedAmount ? `Pay ${formattedAmount} & confirm` : 'Pay & confirm'}
        </button>
      </div>
    `;
  }

  /**
   * A hosted payment page on the gateway's own origin (PayFast / Paystack /
   * Square / legacy GHL). Selected by an `authorizationUrl` — never by a
   * provider name.
   */
  private _renderHostedRedirect() {
    const formattedAmount = this._formatAmount(this.paymentAmount, this.paymentCurrency);

    return html`
      <div class="step-content">
        <h2 class="step-title">Payment</h2>

        ${this.depositType ? html`
          <p class="deposit-label">
            ${this.depositType === 'experience' ? 'Experience Deposit' : 'Security Hold'}
          </p>
        ` : nothing}

        ${this._renderHoldNotice()}

        <div class="hosted-payment-card">
          <div class="hosted-payment-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.11-.9-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
            </svg>
          </div>

          <div class="hosted-payment-amount">${formattedAmount}</div>
          <p class="hosted-payment-desc">${this._getDepositDescription()}</p>

          ${!this._hostedPaymentOpened ? html`
            <button class="hosted-pay-btn" @click=${this._openHostedPayment}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/>
              </svg>
              ${DEPOSIT_COPY.hostedRedirectCta}
            </button>
          ` : html`
            <div class="hosted-poll-status">
              <span class="hosted-poll-dot"></span>
              ${DEPOSIT_COPY.hostedRedirectWaiting}
            </div>
            <button class="hosted-pay-btn" @click=${this._openHostedPayment} style="font-size: 12.8px; padding: 8px 20px;">
              ${DEPOSIT_COPY.hostedRedirectReopen}
            </button>
          `}

          ${this._error ? html`<p class="payment-error">${this._error}</p>` : nothing}
        </div>
      </div>

      <div class="actions">
        <button class="btn btn-secondary" @click=${this._goBack} ?disabled=${this._hostedPolling || this._hostedPaymentOpened}>Back</button>
      </div>
    `;
  }

  /**
   * D6 — RENDER BY HANDLE SHAPE, NOT PROVIDER NAME.
   *
   * A redirect handle wins because it is the more specific signal: whoever
   * issued it (PayFast, Paystack, Square, or the legacy GHL order flow through
   * the deprecated `ghl-payment-url` alias) has already decided the guest pays
   * off-widget. Otherwise we render card fields, which need `clientSecret` +
   * `stripeKey`. `paymentProvider` selects nothing.
   */
  render() {
    if (this._redirectUrl) {
      return this._renderHostedRedirect();
    }
    return this._renderCardFields();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-step-payment': YeboStepPayment;
  }
}
